import { skipToken } from '@reduxjs/toolkit/query/react'
import { TeamMemberRow } from '@numeo/types-main-web-app'
import { useMemo } from 'react'
import { useAppSelector } from '@/hooks'
import { useListTeamMembersQuery } from '@/store/api/teamApi'
import { listMembersFromUsers, normalizeMembersQuery, TeamMembersQuery } from '@/utils/team/teamQuery'
import { listStateOf, READY, TeamListState } from './teamListState'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

interface TeamListOptions {
    /** Ask nothing (a closed modal, a missing id). */
    skip?: boolean
}

/**
 * The company's members (admins, dispatchers, accountants) by name, a page at a time (100 by default), with the server's
 * search across all of them: `GET /team/members/v1`, cached five minutes per query. A company the directory does not serve
 * yet gets the same page computed from `application.users`.
 */
export const useTeamMembers = (query: TeamMembersQuery = {}, options: TeamListOptions = {}): TeamListState<TeamMemberRow> => {
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = JSON.stringify(normalizeMembersQuery(query))
    const normalized = useMemo((): TeamMembersQuery => JSON.parse(key), [key])
    const skip = !!options.skip || (normalized.ids !== undefined && normalized.ids.length === 0)

    const remote = useListTeamMembersQuery(enabled && applicationId && !skip ? { applicationId, ...normalized } : skipToken)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))
    const ownerId = useAppSelector((state) => (state.auth.application?.owner ? String(state.auth.application.owner) : undefined))
    const local = useMemo(() => (enabled || skip ? undefined : listMembersFromUsers(users, ownerId, normalized)), [enabled, skip, users, ownerId, normalized])

    if (!enabled) return listStateOf(local, READY)
    if (skip) return listStateOf(undefined, READY)
    return listStateOf(remote.data, { isLoading: remote.isLoading, isFetching: remote.isFetching, isError: remote.isError })
}
